import React, { createContext, useContext, useState, useEffect } from 'react'

/**
 * Theme Context for managing light/dark mode state.
 * 
 * Provides theme switching functionality and persists user preference
 * in localStorage for consistent experience across sessions.
 */

const ThemeContext = createContext({})

export const useTheme = () => {
  const context = useContext(ThemeContext)
  if (!context) {
    throw new Error('useTheme must be used within a ThemeProvider')
  }
  return context
}

export const ThemeProvider = ({ children }) => {
  const [currentTheme, setCurrentTheme] = useState('light') // 'light', 'dark', 'amoled'
  const [loading, setLoading] = useState(true)

  // Initialize theme from localStorage or system preference
  useEffect(() => {
    initializeTheme()
  }, [])

  // Apply theme to document whenever it changes
  useEffect(() => {
    applyTheme()
  }, [currentTheme])

  /**
   * Initialize theme from localStorage or system preference
   */
  const initializeTheme = () => {
    try {
      const storedTheme = localStorage.getItem('theme')
      
      if (storedTheme && ['light', 'dark', 'amoled'].includes(storedTheme)) {
        // Use stored preference
        setCurrentTheme(storedTheme)
      } else {
        // Use system preference
        const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
        setCurrentTheme(prefersDark ? 'dark' : 'light')
      }
    } catch (error) {
      console.error('Error initializing theme:', error)
      // Default to light mode on error
      setCurrentTheme('light')
    } finally {
      setLoading(false)
    }
  }

  /**
   * Apply theme to document and update status bar color
   */
  const applyTheme = () => {
    if (typeof document !== 'undefined') {
      // Remove all theme classes
      document.documentElement.classList.remove('dark', 'amoled')
      
      // Define theme colors for status bar
      const themeColors = {
        light: '#ffffff',  // White status bar for light mode
        dark: '#1f2937',   // Dark gray for dark mode
        amoled: '#000000'  // Pure black for AMOLED mode
      }
      
      // Add appropriate theme class
      if (currentTheme === 'dark') {
        document.documentElement.classList.add('dark')
      } else if (currentTheme === 'amoled') {
        document.documentElement.classList.add('amoled')
      }
      // light mode doesn't need a class
      
      // Update meta theme-color for status bar
      const themeColorMeta = document.querySelector('meta[name="theme-color"]')
      if (themeColorMeta) {
        themeColorMeta.setAttribute('content', themeColors[currentTheme])
      }
      
      // Update Apple status bar style based on theme
      const appleStatusBarMeta = document.querySelector('meta[name="apple-mobile-web-app-status-bar-style"]')
      if (appleStatusBarMeta) {
        const statusBarStyles = {
          light: 'default',           // Dark text on light background
          dark: 'black-translucent',  // Light text on dark background
          amoled: 'black-translucent' // Light text on black background
        }
        appleStatusBarMeta.setAttribute('content', statusBarStyles[currentTheme])
      }
    }
  }

  /**
   * Cycle through light → dark → amoled → light
   */
  const toggleTheme = () => {
    const themeOrder = ['light', 'dark', 'amoled']
    const currentIndex = themeOrder.indexOf(currentTheme)
    const nextIndex = (currentIndex + 1) % themeOrder.length
    const newTheme = themeOrder[nextIndex]
    
    setCurrentTheme(newTheme)
    
    try {
      localStorage.setItem('theme', newTheme)
    } catch (error) {
      console.error('Error saving theme preference:', error)
    }
  }

  /**
   * Set specific theme mode
   */
  const setTheme = (themeName) => {
    if (['light', 'dark', 'amoled'].includes(themeName)) {
      setCurrentTheme(themeName)
      
      try {
        localStorage.setItem('theme', themeName)
      } catch (error) {
        console.error('Error saving theme preference:', error)
      }
    }
  }

  /**
   * Get current theme name
   */
  const getThemeName = () => {
    return currentTheme
  }

  /**
   * Check if current theme is dark (for backward compatibility)
   */
  const isDarkMode = currentTheme === 'dark' || currentTheme === 'amoled'

  /**
   * Check if current theme is AMOLED
   */
  const isAmoledMode = currentTheme === 'amoled'

  const value = {
    currentTheme,
    isDarkMode,
    isAmoledMode,
    loading,
    toggleTheme,
    setTheme,
    getThemeName
  }

  return (
    <ThemeContext.Provider value={value}>
      {children}
    </ThemeContext.Provider>
  )
}

export default ThemeContext 